/**
 * Stripe Payment Service
 *
 * Handles Stripe integration for payment processing
 * Uses environment variables for configuration
 *
 * Security: Only public keys are used in frontend
 * All secret keys and webhook secrets are handled on the backend
 *
 * @updated Issue #286対応 - Stripe.js完全統合
 */

import * as Sentry from '@sentry/react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import env from '../config/env';
import logger from '../utils/logger';
import { AuthApiClient } from './auth/shared/apiClient';
import { PLAN_PRICES } from '../constants/subscriptionPlans';

class StripeService {
  private stripePromise: Promise<Stripe | null> | null = null;
  private publicKey: string | undefined;

  constructor() {
    this.publicKey = env.get('STRIPE_PUBLIC_KEY');

    // Log warning in development if Stripe is not configured
    if (!this.publicKey && env.isDev()) {
      logger.warn(
        'Stripe public key is not configured. Payment features will be disabled.'
      );
    }
  }

  /**
   * Initialize Stripe.js
   * Dynamically loads Stripe library only when needed
   * @returns Promise<Stripe | null>
   */
  async initializeStripe(): Promise<Stripe | null> {
    if (!this.publicKey) {
      if (env.isDev()) {
        logger.warn('Stripe is not configured. Using development mode.');
        return null;
      }
      throw new Error(
        'Stripe is not configured. Please set VITE_STRIPE_PUBLIC_KEY in your environment.'
      );
    }

    if (!this.stripePromise) {
      this.stripePromise = loadStripe(this.publicKey);
    }
    return this.stripePromise;
  }

  /**
   * Check if Stripe is configured and available
   */
  isConfigured(): boolean {
    return !!this.publicKey;
  }

  /**
   * Create a checkout session for subscription plans
   * This should call your backend API which uses the Stripe secret key
   *
   * @param items Array of price items for checkout
   * @param options Additional options (mode, success_url, cancel_url, etc.)
   */
  async createCheckoutSession(
    items: Array<{ price: string; quantity: number }>,
    options?: {
      mode?: 'payment' | 'subscription';
      successUrl?: string;
      cancelUrl?: string;
      customerEmail?: string;
      metadata?: Record<string, string>;
    }
  ): Promise<{ sessionId: string; url: string }> {
    if (!this.isConfigured()) {
      // 開発環境用のモック
      if (env.isDev()) {
        logger.info('Mock Stripe Checkout Session created:', {
          items,
          options,
        });
        return {
          sessionId: 'mock_session_' + Date.now(),
          url: '/settings?mock_checkout=true&plan=' + items[0]?.price,
        };
      }
      throw new Error('Stripe is not configured');
    }

    // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
    const token = await AuthApiClient.getAuthToken();
    // Call your backend API to create the session
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/stripe/create-checkout-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items,
          mode: options?.mode || 'subscription',
          success_url:
            options?.successUrl ||
            `${window.location.origin}/settings?success=true`,
          cancel_url:
            options?.cancelUrl ||
            `${window.location.origin}/settings?canceled=true`,
          customer_email: options?.customerEmail,
          metadata: options?.metadata,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(
        `Failed to create checkout session: ${errorText}`
      );
      Sentry.captureException(error, { extra: { status: response.status } });
      throw error;
    }

    return response.json();
  }

  /**
   * Redirect to Stripe Customer Portal
   * This should call your backend API which creates the portal session
   *
   * @param options Portal session options
   */
  async redirectToCustomerPortal(options?: {
    returnUrl?: string;
    configuration?: string;
  }): Promise<void> {
    if (!this.isConfigured()) {
      // 開発環境用のモック
      if (env.isDev()) {
        logger.info('Mock Stripe Customer Portal redirect');
        window.location.href = '/settings?mock_portal=true';
        return;
      }
      throw new Error('Stripe is not configured');
    }

    // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
    const token = await AuthApiClient.getAuthToken();
    // Call your backend API to create the portal session
    // Issue #673: Fixed API URL to use versioned endpoint
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/v1/payments/subscription/portal-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          return_url:
            options?.returnUrl || `${window.location.origin}/settings`,
          configuration: options?.configuration,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Failed to create portal session: ${errorText}`);
      Sentry.captureException(error, { extra: { status: response.status } });
      throw error;
    }

    const { url } = await response.json();
    window.location.href = url;
  }

  /**
   * Handle payment method setup
   * Used for saving payment methods for future use
   */
  async setupPaymentMethod(): Promise<unknown> {
    const stripe = await this.initializeStripe();

    // Call your backend API to create a setup intent
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/stripe/create-setup-intent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create setup intent');
    }

    const { clientSecret } = await response.json();

    // Use Stripe.js to handle the payment method setup
    return stripe?.confirmCardSetup(clientSecret);
  }

  /**
   * Process a payment
   * Used for immediate payment collection
   */
  async processPayment(
    amount: number,
    currency: string = 'jpy'
  ): Promise<unknown> {
    const stripe = await this.initializeStripe();

    // Call your backend API to create a payment intent
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/stripe/create-payment-intent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount, currency }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    const { clientSecret } = await response.json();

    // Use Stripe.js to handle the payment
    return stripe?.confirmCardPayment(clientSecret);
  }

  /**
   * Get payment methods for the current customer
   * This should call your backend API
   */
  async getPaymentMethods(): Promise<unknown[]> {
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/stripe/payment-methods`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch payment methods');
    }

    return response.json();
  }

  /**
   * Development mode mock functions
   * Used when Stripe is not configured or in development
   */
  getMockPaymentMethods() {
    if (!env.isDev()) {
      throw new Error('Mock payment methods are only available in development');
    }

    return [
      {
        id: 'pm_mock_1',
        type: 'card',
        card: {
          brand: 'visa',
          last4: '4242',
          exp_month: 12,
          exp_year: 2025,
        },
        created: new Date().toISOString(),
        isDefault: true,
      },
    ];
  }

  /**
   * Get current subscription status
   * Fetches the current subscription from the backend
   *
   * @returns Current subscription details
   */
  async getSubscriptionStatus(): Promise<{
    id: string;
    status: 'active' | 'past_due' | 'canceled' | 'unpaid';
    current_period_end: number;
    plan: {
      id: string;
      product: string;
      amount: number;
      currency: string;
      interval: 'month' | 'year';
    };
  } | null> {
    if (!this.isConfigured()) {
      // 開発環境用のモック
      if (env.isDev()) {
        return {
          id: 'sub_mock_' + Date.now(),
          status: 'active',
          current_period_end: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days from now
          plan: {
            id: 'price_light',
            product: 'prod_light',
            amount: PLAN_PRICES.light.monthly,
            currency: 'jpy',
            interval: 'month',
          },
        };
      }
      return null;
    }

    // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
    const token = await AuthApiClient.getAuthToken();
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/stripe/subscription`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No subscription
      }
      const error = new Error('Failed to fetch subscription status');
      Sentry.captureException(error, { extra: { status: response.status } });
      throw error;
    }

    return response.json();
  }

  /**
   * Cancel subscription
   * Cancels the current subscription at period end
   *
   * @param immediately If true, cancels immediately instead of at period end
   */
  async cancelSubscription(immediately = false): Promise<void> {
    if (!this.isConfigured()) {
      if (env.isDev()) {
        logger.info('Mock subscription cancellation', { immediately });
        return;
      }
      throw new Error('Stripe is not configured');
    }

    // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
    const token = await AuthApiClient.getAuthToken();
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/stripe/subscription/cancel`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ immediately }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Failed to cancel subscription: ${errorText}`);
      Sentry.captureException(error, {
        extra: { immediately, status: response.status },
      });
      throw error;
    }
  }

  /**
   * Update subscription
   * Changes the subscription plan
   *
   * @param newPriceId The new Stripe Price ID
   * @param prorationBehavior How to handle prorations
   */
  async updateSubscription(
    newPriceId: string,
    prorationBehavior:
      | 'create_prorations'
      | 'none'
      | 'always_invoice' = 'create_prorations'
  ): Promise<void> {
    if (!this.isConfigured()) {
      if (env.isDev()) {
        logger.info('Mock subscription update', {
          newPriceId,
          prorationBehavior,
        });
        return;
      }
      throw new Error('Stripe is not configured');
    }

    // Issue #1425: Use AuthApiClient.getAuthToken() instead of localStorage.getItem('token')
    const token = await AuthApiClient.getAuthToken();
    const response = await fetch(
      `${env.get('API_BASE_URL')}/api/stripe/subscription/update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          price: newPriceId,
          proration_behavior: prorationBehavior,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`Failed to update subscription: ${errorText}`);
      Sentry.captureException(error, {
        extra: { newPriceId, prorationBehavior, status: response.status },
      });
      throw error;
    }
  }
}

// Export singleton instance
const stripeService = new StripeService();
export default stripeService;

// Export class for testing
export { StripeService };
